import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["eventRow", "typeCheckbox", "ahoyEventNameCheckbox", "subscriptionActorCheckbox", "pagination"];
  static values = { 
    itemsPerPage: { type: Number, default: 25 },
    currentPage: { type: Number, default: 1 }
  };

  connect() {
    this.updateFilter();
  }

  typeChanged(event) {
    this.currentPageValue = 1;
    this.updateFilter();
  }

  filterChanged() {
    this.currentPageValue = 1;
    this.updateFilter();
  }

  nextPage(event) {
    event.preventDefault();
    const filteredRows = this.getFilteredRows();
    const totalPages = Math.max(1, Math.ceil(filteredRows.length / this.itemsPerPageValue));
    if (this.currentPageValue < totalPages) {
      this.currentPageValue = this.currentPageValue + 1;
      this.updateFilter();
    }
  }

  prevPage(event) {
    event.preventDefault();
    if (this.currentPageValue > 1) {
      this.currentPageValue = this.currentPageValue - 1;
      this.updateFilter();
    }
  }

  goToPage(event) {
    event.preventDefault();
    const page = parseInt(event.target.dataset.page);
    const filteredRows = this.getFilteredRows();
    const totalPages = Math.max(1, Math.ceil(filteredRows.length / this.itemsPerPageValue));
    if (page >= 1 && page <= totalPages) {
      this.currentPageValue = page;
      this.updateFilter();
    }
  }

  getTotalPages() {
    const visibleRows = this.getVisibleRows();
    return Math.max(1, Math.ceil(visibleRows.length / this.itemsPerPageValue));
  }

  getVisibleRows() {
    return Array.from(this.eventRowTargets).filter(row => row.style.display !== "none");
  }

  getSelectedFilters() {
    const selectedTypes = new Set();
    this.typeCheckboxTargets.forEach((checkbox) => {
      if (checkbox.checked) {
        selectedTypes.add(checkbox.value);
      }
    });

    const selectedAhoyEventNames = new Set();
    if (selectedTypes.has("ahoy_event") && this.hasAhoyEventNameCheckboxTarget) {
      this.ahoyEventNameCheckboxTargets.forEach((checkbox) => {
        if (checkbox.checked) {
          selectedAhoyEventNames.add(checkbox.value);
        }
      });
    }

    const selectedSubscriptionActorTypes = new Set();
    if (this.hasSubscriptionActorCheckboxTarget) {
      this.subscriptionActorCheckboxTargets.forEach((checkbox) => {
        if (checkbox.checked) {
          selectedSubscriptionActorTypes.add(checkbox.value);
        }
      });
    }

    return { selectedTypes, selectedAhoyEventNames, selectedSubscriptionActorTypes };
  }

  rowMatchesFilter(row, filters) {
    const { selectedTypes, selectedAhoyEventNames, selectedSubscriptionActorTypes } = filters;
    const eventType = row.dataset.eventType;
    const eventName = row.dataset.eventName || "";
    const actorType = row.dataset.actorType;
    
    if (!selectedTypes.has(eventType)) {
      return false;
    }

    if (eventType === "ahoy_event") {
      return selectedAhoyEventNames.size > 0 && selectedAhoyEventNames.has(eventName);
    }

    if (eventType === "subscription_change") {
      return selectedSubscriptionActorTypes.size > 0 && selectedSubscriptionActorTypes.has(actorType);
    }

    return true;
  }

  getFilteredRows() {
    const filters = this.getSelectedFilters();
    return Array.from(this.eventRowTargets).filter(row => this.rowMatchesFilter(row, filters));
  }

  updateFilter() {
    const visibleRows = this.getFilteredRows();

    const totalPages = Math.max(1, Math.ceil(visibleRows.length / this.itemsPerPageValue));
    const startIndex = (this.currentPageValue - 1) * this.itemsPerPageValue;
    const endIndex = startIndex + this.itemsPerPageValue;

    this.eventRowTargets.forEach((row) => {
      row.style.display = "none";
    });

    visibleRows.forEach((row, index) => {
      if (index >= startIndex && index < endIndex) {
        row.style.display = "";
      }
    });

    this.updatePagination(totalPages, visibleRows.length);
  }

  updatePagination(totalPages, visibleCount) {
    if (!this.hasPaginationTarget) {
      return;
    }

    const currentPage = this.currentPageValue;
    
    if (totalPages <= 1) {
      this.paginationTarget.innerHTML = "";
      return;
    }

    let paginationHTML = '<div class="pagination d-flex gap-2 align-items-center">';
    if (currentPage > 1) {
      paginationHTML += `<button class="btn btn-sm btn-outline-secondary" data-action="click->audit-log-filter#prevPage">Previous</button>`;
    } else {
      paginationHTML += `<button class="btn btn-sm btn-outline-secondary" disabled>Previous</button>`;
    }

    // Page numbers
    const maxPagesToShow = 10;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    if (startPage > 1) {
      paginationHTML += `<button class="btn btn-sm btn-outline-secondary" data-action="click->audit-log-filter#goToPage" data-page="1">1</button>`;
      if (startPage > 2) {
        paginationHTML += `<span class="px-2">...</span>`;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      if (i === currentPage) {
        paginationHTML += `<button class="btn btn-sm btn-primary" disabled>${i}</button>`;
      } else {
        paginationHTML += `<button class="btn btn-sm btn-outline-secondary" data-action="click->audit-log-filter#goToPage" data-page="${i}">${i}</button>`;
      }
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        paginationHTML += `<span class="px-2">...</span>`;
      }
      paginationHTML += `<button class="btn btn-sm btn-outline-secondary" data-action="click->audit-log-filter#goToPage" data-page="${totalPages}">${totalPages}</button>`;
    }

    // Next button
    if (currentPage < totalPages) {
      paginationHTML += `<button class="btn btn-sm btn-outline-secondary" data-action="click->audit-log-filter#nextPage">Next</button>`;
    } else {
      paginationHTML += `<button class="btn btn-sm btn-outline-secondary" disabled>Next</button>`;
    }

    paginationHTML += '</div>';
    this.paginationTarget.innerHTML = paginationHTML;
  }
}
